// Permission actions
export const PERMISSION_ACTIONS = {
  GET: 'get',
  WRITE: 'write',
  DELETE: 'delete',
} as const;

// Type definitions
export type Permission = (typeof PERMISSION_ACTIONS)[keyof typeof PERMISSION_ACTIONS];

const rg =
  <G extends string>(group: G) =>
  <Ks extends string[]>(...keys: Ks): { [P in Ks[number]]: `${G}.${P}` } =>
    Object.fromEntries(keys.map((k) => [k, `${group}.${k}`])) as never;

export const RESOURCES = {
  COURSE: rg('COURSE')(
    'MAIN',
    'CONTENT',
    'GAMIFICATION',
    'TARIFFS',
    'SETTINGS',
    'PUBLIC_PAGE',
    'VIDEO_DOWNLOAD'
  ),
  CATEGORY: rg('CATEGORY')('MAIN'),
  LECTURE: rg('LECTURE')('CONTENT', 'PRACTICE', 'HOMEWORK', 'SETTINGS', 'AI_REPORT'),
  EXAM: rg('EXAM')('QUESTIONS', 'SETTINGS', 'CREATE_FROM_FILE', 'CREATE_FROM_LECTURES'),
  STREAM: rg('STREAM')('MAIN', 'STUDENTS', 'SETTINGS', 'LINKS'),
  GROUP: rg('GROUP')('MAIN', 'STUDENTS', 'LINKS'),
  COURSE_COLLECTION: rg('COURSE_COLLECTION')('MAIN', 'COURSES', 'STUDENTS'),
  STUDENT: rg('STUDENT')('MAIN', 'PROFILE', 'IMPORT'),
  SUBMISSION: rg('SUBMISSION')('MAIN'),
  FEEDBACK: rg('FEEDBACK')('MAIN'),
  EXAM_SUBMISSION: rg('EXAM_SUBMISSION')('MAIN', 'PRACTICE'),
  MEMBER: rg('MEMBER')('MAIN', 'PROFILE', 'ROLES', 'ASSIGN'),
  ANALYTICS: rg('ANALYTICS')('MAIN', 'POINTS', 'PROGRESS', 'MEMBERS', 'CONTENT', 'TRAINING_CONTROL'),
  AUDIT_LOG: rg('AUDIT_LOG')('MAIN'),
  PROFILE_SETTINGS: rg('PROFILE_SETTINGS')('MAIN'),
  PLATFORM_SETTINGS: rg('PLATFORM_SETTINGS')(
    'MAIN',
    'GENERAL',
    'CONTACTS',
    'COLORS',
    'IMAGES',
    'BRAND',
    'DOCUMENTS',
    'PUBLIC_PAGES'
  ),
  PAYMENT: rg('PAYMENT')('MAIN'),
  SALES_HISTORY: rg('SALES_HISTORY')('MAIN'),
  SUBSCRIPTION: rg('SUBSCRIPTION')('MAIN'),
  CHATBOT: rg('CHATBOT')('MAIN', 'COURSES', 'SETTINGS'),
  SURVEY: rg('SURVEY')('MAIN', 'ANALYTICS', 'RESPONSES'),
  CHAT: rg('CHAT')('MAIN', 'DIRECT'),
  AI_ORCHESTRATOR: rg('AI_ORCHESTRATOR')('MAIN'),
  AI_IMAGE: rg('AI_IMAGE')('MAIN'),
  LIVE_SESSION: rg('LIVE_SESSION')('MAIN', 'ATTENDANCE'),
  INTEGRATION: rg('INTEGRATION')('MAIN'),
  ORG_STRUCTURE: rg('ORG_STRUCTURE')('UNITS', 'POSITIONS'),
  ASSIGNMENT: rg('ASSIGNMENT')('MAIN'),
  MCP: rg('MCP')('MAIN', 'CONNECTIONS'),
};

type PermissionsShape<T> = {
  [K in keyof T]: T[K] extends string ? readonly Permission[] : PermissionsShape<T[K]>;
};

export type PermissionMatrix = PermissionsShape<typeof RESOURCES>;

export const PERMISSIONS_SCHEMA = {
  COURSE: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
    CONTENT: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
    GAMIFICATION: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    TARIFFS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
    SETTINGS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    PUBLIC_PAGE: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    VIDEO_DOWNLOAD: [PERMISSION_ACTIONS.GET],
  },
  CATEGORY: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
  },
  LECTURE: {
    CONTENT: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    PRACTICE: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    HOMEWORK: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    SETTINGS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    AI_REPORT: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
  },
  EXAM: {
    QUESTIONS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    SETTINGS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    CREATE_FROM_FILE: [PERMISSION_ACTIONS.GET],
    CREATE_FROM_LECTURES: [PERMISSION_ACTIONS.GET],
  },
  STREAM: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
    STUDENTS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
    SETTINGS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    LINKS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
  },
  GROUP: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
    STUDENTS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
    LINKS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
  },
  COURSE_COLLECTION: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
    COURSES: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
    STUDENTS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
  },
  STUDENT: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    PROFILE: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
    IMPORT: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
  },
  SUBMISSION: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
  },
  FEEDBACK: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
  },
  EXAM_SUBMISSION: {
    MAIN: [PERMISSION_ACTIONS.GET],
    PRACTICE: [PERMISSION_ACTIONS.GET],
  },
  MEMBER: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.DELETE],
    PROFILE: [PERMISSION_ACTIONS.GET],
    ROLES: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
    ASSIGN: [PERMISSION_ACTIONS.WRITE],
  },
  ANALYTICS: {
    MAIN: [PERMISSION_ACTIONS.GET],
    POINTS: [PERMISSION_ACTIONS.GET],
    PROGRESS: [PERMISSION_ACTIONS.GET],
    MEMBERS: [PERMISSION_ACTIONS.GET],
    CONTENT: [PERMISSION_ACTIONS.GET],
    TRAINING_CONTROL: [PERMISSION_ACTIONS.GET],
  },
  AUDIT_LOG: {
    MAIN: [PERMISSION_ACTIONS.GET],
  },
  PROFILE_SETTINGS: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
  },
  PLATFORM_SETTINGS: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.DELETE],
    GENERAL: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    CONTACTS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
    COLORS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    IMAGES: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    BRAND: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    DOCUMENTS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
    PUBLIC_PAGES: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
  },
  PAYMENT: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
  },
  SALES_HISTORY: {
    MAIN: [PERMISSION_ACTIONS.GET],
  },
  SUBSCRIPTION: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
  },
  CHATBOT: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    COURSES: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    SETTINGS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
  },
  SURVEY: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
    ANALYTICS: [PERMISSION_ACTIONS.GET],
    RESPONSES: [PERMISSION_ACTIONS.GET],
  },
  CHAT: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    DIRECT: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
  },
  AI_ORCHESTRATOR: {
    MAIN: [PERMISSION_ACTIONS.GET],
  },
  AI_IMAGE: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
  },
  LIVE_SESSION: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE],
    ATTENDANCE: [PERMISSION_ACTIONS.GET],
  },
  INTEGRATION: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
  },
  ORG_STRUCTURE: {
    UNITS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
    POSITIONS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
  },
  ASSIGNMENT: {
    MAIN: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.WRITE, PERMISSION_ACTIONS.DELETE],
  },
  MCP: {
    MAIN: [PERMISSION_ACTIONS.GET],
    CONNECTIONS: [PERMISSION_ACTIONS.GET, PERMISSION_ACTIONS.DELETE],
  },
} as const satisfies PermissionMatrix;

type SchemaType = typeof PERMISSIONS_SCHEMA;

export type AllowedActionsForPath<P extends string> = P extends `${infer G}.${infer K}`
  ? G extends keyof SchemaType
    ? K extends keyof SchemaType[G]
      ? SchemaType[G][K] extends readonly (infer A)[]
        ? A & Permission
        : never
      : never
    : never
  : never;

export type ResourcePathAllowing<A extends Permission> = {
  [P in ResourcePath]: A extends AllowedActionsForPath<P> ? P : never;
}[ResourcePath];

export type ResourceGroup = keyof PermissionMatrix;
export type ResourceKey<G extends ResourceGroup> = keyof PermissionMatrix[G];

export type ResourcePath = {
  [G in ResourceGroup]: `${G}.${Extract<ResourceKey<G>, string>}`;
}[ResourceGroup];

export type PermissionMatrixLike = { [G in ResourceGroup]?: Record<string, Permission[]> };

export const hasPermission = (
  matrix: PermissionMatrixLike | null | undefined,
  path: string,
  action: Permission
): boolean => {
  if (!matrix) return false;
  const dot = path.indexOf('.');
  const group = path.slice(0, dot) as ResourceGroup;
  const key = path.slice(dot + 1);
  return (matrix[group]?.[key] ?? []).includes(action);
};
